import type { AccountType, SignupAccountType } from '@curentia/shared';

/**
 * The app's four doors. Each signs up and signs in its own kind of account.
 * Pharmacy accounts are pharmacists; clinic staff get their practice roles
 * from the practice later.
 */
export const DOORS = {
  patient: {
    type: 'patient',
    label: 'Patient',
    blurb: 'Keep your prescriptions and readings, and decide who sees your history.',
  },
  doctor: {
    type: 'doctor',
    label: 'Doctor',
    blurb: 'See history your patients share, and send prescriptions.',
    registration: { council: 'Medical council', number: 'Registration number' },
  },
  pharmacy: {
    type: 'pharmacist',
    label: 'Pharmacy',
    blurb: 'Receive prescriptions and record what you dispense.',
    registration: { council: 'Pharmacy council', number: 'Registration number' },
  },
  staff: {
    type: 'staff',
    label: 'Clinic staff',
    blurb: 'Front desk and practice administration.',
  },
} as const satisfies Record<string, { type: SignupAccountType; label: string; blurb: string; registration?: { council: string; number: string } }>;

export type Door = keyof typeof DOORS;

export function isDoor(value: unknown): value is Door {
  return typeof value === 'string' && value in DOORS;
}

export function doorOf(type: AccountType): Door | null {
  return (Object.keys(DOORS) as Door[]).find((door) => DOORS[door].type === type) ?? null;
}

/** A short name for this device in the account's device list. */
export function deviceLabel(userAgent: string): string {
  const os = /Android/i.test(userAgent)
    ? 'Android'
    : /iPhone|iPad|iPod/i.test(userAgent)
      ? 'iPhone or iPad'
      : /Mac OS X/i.test(userAgent)
        ? 'Mac'
        : /Windows/i.test(userAgent)
          ? 'Windows'
          : /Linux/i.test(userAgent)
            ? 'Linux'
            : 'Device';
  return `Curentia on ${os}`;
}
